import {
  IllustrationSmallChildCare,
  IllustrationMediumChildCare,
  IllustrationSmallSeniorCare,
  IllustrationMediumSeniorCare,
  IllustrationSmallAdultCare,
  IllustrationMediumAdultCare,
  IllustrationSmallPetCare,
  IllustrationMediumPetCare,
  IllustrationSmallHousekeeping,
  IllustrationMediumHousekeeping,
  IllustrationSmallTutoring,
  IllustrationMediumTutoring,
  IllustrationSmallDayCare,
  IllustrationMediumDayCare,
} from '@care/react-embrace-icons';
import { ServiceId } from '@/constants';

/**
 * Returns the side illustration based on the provided SEO segment ID and service ID.
 * @param {Object} params - The parameters object.
 * @param {string} params.seoSegmentId - The SEO segment ID.
 * @param {string} params.serviceId - The service ID.
 * @returns {Record<string, JSX.Element> | null} - A record of JSX elements representing the side illustrations, or null if no matching illustrations are found.
 */

export const getSideIllustration = ({
  seoSegmentId,
  serviceId,
}: {
  seoSegmentId: string;
  serviceId: string;
}): Record<string, JSX.Element> | null => {
  const segmentId = `/${seoSegmentId.split('/')[1]}`;

  const childCareIllustrations: Record<string, JSX.Element> = Object.freeze({
    small: <IllustrationSmallChildCare />,
    medium: <IllustrationMediumChildCare />,
  });

  const seniorCareIllustrations: Record<string, JSX.Element> = Object.freeze({
    small: <IllustrationSmallSeniorCare />,
    medium: <IllustrationMediumSeniorCare />,
  });

  const adultCareIllustrations: Record<string, JSX.Element> = Object.freeze({
    small: <IllustrationSmallAdultCare />,
    medium: <IllustrationMediumAdultCare />,
  });

  const petCareIllustrations: Record<string, JSX.Element> = Object.freeze({
    small: <IllustrationSmallPetCare />,
    medium: <IllustrationMediumPetCare />,
  });

  const housekeepingIllustrations: Record<string, JSX.Element> = Object.freeze({
    small: <IllustrationSmallHousekeeping />,
    medium: <IllustrationMediumHousekeeping />,
  });

  const tutoringIllustrations: Record<string, JSX.Element> = Object.freeze({
    small: <IllustrationSmallTutoring />,
    medium: <IllustrationMediumTutoring />,
  });

  const dayCareIllustrations: Record<string, JSX.Element> = Object.freeze({
    small: <IllustrationSmallDayCare />,
    medium: <IllustrationMediumDayCare />,
  });

  const sideIllustrationMapForVerticals: Record<
    string,
    Record<string, JSX.Element>
  > = Object.freeze({
    [ServiceId.CHILDCARE]: childCareIllustrations,
    [ServiceId.SENIRCARE]: seniorCareIllustrations,
    [ServiceId.PETCAREXX]: petCareIllustrations,
    [ServiceId.HOUSEKEEP]: housekeepingIllustrations,
    [ServiceId.TUTORINGX]: tutoringIllustrations,
  });

  const sideIllustrationMapForSegmentIDs: Record<
    string,
    Record<string, JSX.Element>
  > = Object.freeze({
    '/adult-care': adultCareIllustrations,
    '/day-care': dayCareIllustrations,
  });

  return (
    sideIllustrationMapForSegmentIDs[segmentId] ??
    sideIllustrationMapForVerticals[serviceId] ??
    null
  );
};
